import isWebview from 'is-ua-webview';

import { translateInternalFailureCode } from '@/lib/citibox/api/BackendCodesTransations';
import { AndroidStrategy } from '@/lib/citibox/sdk/strategies/AndroidStrategy';
import { IosStrategy } from '@/lib/citibox/sdk/strategies/IosStrategy';
import { UndefinedStrategy } from '@/lib/citibox/sdk/strategies/UndefinedStrategy';
import { WebViewStrategy } from '@/lib/citibox/sdk/strategies/WebViewStrategy';
import { finishFeature } from '@/lib/features';

export const SDK_SUPPORTED_CHARACTERISTICS = {
  BLE_LOCK: 'lock_ble',
  BLE_ACCESS: 'access_ble',
  BLE: 'ble'
};

export const BLE_PERMISSION_STATUS = {
  BLE_PERMISSION_GRANTED: 'ble_permissions_granted',
  BLE_PERMISSION_DENIED: 'ble_permissions_denied',
  BLE_NOT_AVAILABLE: 'ble_not_available'
};

export function isSDK() {
  if (typeof window === 'undefined' && !isWebview(window.navigator.userAgent)) {
    return false;
  }
  return !(getSDKStrategy() instanceof UndefinedStrategy);
}

export function getSDKStrategy() {
  if (window.CitiboxCourierSDK) {
    return new AndroidStrategy();
  }
  if (window.webkit?.messageHandlers) {
    return new IosStrategy();
  }
  if (isWebview(window.navigator.userAgent)) {
    return new WebViewStrategy();
  }
  return new UndefinedStrategy();
}

function characteristicsSDK() {
  return getSDKStrategy().characteristicsSDK();
}

function sdkInfo() {
  return getSDKStrategy().sdkInfo();
}

function sessionToken(sessionToken) {
  return getSDKStrategy().onSessionToken(sessionToken);
}

function hasBLEOpeningLockCharacteristic() {
  return _hasBLESDKCharacteristic(SDK_SUPPORTED_CHARACTERISTICS.BLE_LOCK);
}

function hasBLEOpeningDoorCharacteristic() {
  return _hasBLESDKCharacteristic(SDK_SUPPORTED_CHARACTERISTICS.BLE_ACCESS);
}

function hasOpeningBLECharacteristic() {
  return _hasBLESDKCharacteristic(SDK_SUPPORTED_CHARACTERISTICS.BLE);
}

function _hasBLESDKCharacteristic(characteristic) {
  const bleStatus = localStorage.getItem('bleStatus') || '';
  if (bleStatus !== BLE_PERMISSION_STATUS.BLE_PERMISSION_GRANTED) {
    // eslint-disable-next-line no-console
    console.warn('bleStatus is not granted! ' + bleStatus + ' for ' + characteristic);
    return false;
  }
  const characteristicsSDK = (localStorage.getItem('characteristicsSDK') || '').split('|');
  if (characteristicsSDK.indexOf(characteristic) === -1) {
    // eslint-disable-next-line no-console
    console.warn('characteristicsSDK does not have ' + characteristic + ' : ' + characteristicsSDK.toString());
    return false;
  }
  return true;
}

function retrieveTransactionData() {
  // TODO Migrate all localStorage access to citiboxData
  const citiboxId = Number(localStorage.getItem('citiboxId')) || localStorage.getItem('transactionId');
  const deliveryId = localStorage.getItem('expeditionId') || localStorage.getItem('deliveryId') || '';
  return { citiboxId, deliveryId };
}

function success(feature) {
  const { citiboxId, deliveryId } = retrieveTransactionData();
  // eslint-disable-next-line no-console
  console.log(`CitiboxId: ${citiboxId} DeliveryId: ${deliveryId}`);
  const boxNumber = Number(localStorage.getItem('boxNumber'));
  finishFeature(feature, {
    delivery_id: deliveryId,
    transaction_id: citiboxId
  }).then(() => {
    getSDKStrategy().onSuccess(boxNumber, citiboxId, deliveryId);
    window.location.href = '/finish?method=success&boxNumber=' + boxNumber + '&citiboxId=' + citiboxId + '&deliveryId=' + deliveryId;
  });
}

function error(feature, errorCode) {
  const { citiboxId, deliveryId } = retrieveTransactionData();
  finishFeature(feature, { delivery_id: deliveryId, transaction_id: citiboxId }, errorCode).then(() => {
    getSDKStrategy().onError(errorCode);
    window.location.href = '/finish?method=error&code=' + errorCode;
  });
}

function fail(feature, failureCode) {
  const { citiboxId, deliveryId } = retrieveTransactionData();
  finishFeature(feature, { delivery_id: deliveryId, transaction_id: citiboxId }, translateInternalFailureCode(failureCode)).then(() => {
    getSDKStrategy().onFail(translateInternalFailureCode(failureCode));
    window.location.href = '/finish?method=fail&code=' + failureCode;
  });
}

function cancel(feature, cancelCode) {
  const { citiboxId, deliveryId } = retrieveTransactionData();
  finishFeature(feature, { delivery_id: deliveryId, transaction_id: citiboxId }, cancelCode).then(() => {
    getSDKStrategy().onCancel(cancelCode);
    window.location.href = '/finish?method=cancel&code=' + cancelCode;
  });
}

function openBLE(url, mac, ssid, serviceUUID, scommandUUID, sreplyUUID, openToken, otaServiceUUID, otaControlUUID, otaDataUUID) {
  getSDKStrategy().openBLE(url, mac, ssid, serviceUUID, scommandUUID, sreplyUUID, openToken, otaServiceUUID, otaControlUUID, otaDataUUID);
}

function cancelOpenBLE() {
  getSDKStrategy().cancelOpenBLE();
}

function blePermissionsStatus() {
  getSDKStrategy().blePermissionsStatus();
}

export const CitiboxMobileSDK = {
  cancel,
  error,
  fail,
  success,
  openBLE,
  cancelOpenBLE,
  blePermissionsStatus,
  characteristicsSDK,
  hasBLEOpeningLockCharacteristic,
  hasBLEOpeningDoorCharacteristic,
  hasOpeningBLECharacteristic,
  sdkInfo,
  isSDK,
  sessionToken
};
